import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { usePartialPayments } from "@/hooks/usePartialPayments";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { printPartialPaymentReceipt, getPrinterForPartialPayment } from "@/lib/partial-payment-printing";
import {
  Banknote,
  CreditCard,
  Smartphone,
  Loader2,
  Printer,
} from "lucide-react";

interface PartialPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderNumber: number;
  tableNumber?: string;
  tabNumber?: string;
  commandNumber?: string;
  remainingAmount: number;
  onSuccess?: () => void;
}

const paymentTypeIcons = {
  cash: Banknote,
  card: CreditCard,
  digital_wallet: Smartphone,
};

export const PartialPaymentDialog = ({
  open,
  onOpenChange,
  orderId,
  orderNumber,
  tableNumber,
  tabNumber,
  commandNumber,
  remainingAmount,
  onSuccess,
}: PartialPaymentDialogProps) => {
  const { activeRestaurant } = useRestaurantContext();
  const { paymentMethods } = usePaymentMethods();
  const { createPartialPayment } = usePartialPayments(orderId);

  const [selectedMethodId, setSelectedMethodId] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [payerName, setPayerName] = useState("");
  const [printReceipt, setPrintReceipt] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partialPaymentPrinter, setPartialPaymentPrinter] = useState<{ printerName: string; charsPerLine: number } | null>(null);

  const activePaymentMethods = paymentMethods.filter((m) => m.is_active);

  // Check if there's a printer configured for partial_payment
  useEffect(() => {
    if (activeRestaurant?.id) {
      getPrinterForPartialPayment(activeRestaurant.id).then(setPartialPaymentPrinter);
    }
  }, [activeRestaurant?.id]);

  const selectedMethod = activePaymentMethods.find(
    (m) => m.id === selectedMethodId
  );

  const numericAmount = parseFloat(amount.replace(",", ".")) || 0;
  const feeAmount = selectedMethod?.fee_percentage
    ? (numericAmount * (selectedMethod.fee_percentage / 100))
    : 0;

  const isValid =
    selectedMethodId &&
    numericAmount > 0 &&
    numericAmount <= remainingAmount;

  useEffect(() => {
    if (open) {
      setSelectedMethodId("");
      setAmount("");
      setPayerName("");
      setPrintReceipt(true);
    }
  }, [open]);

  const formatCurrency = (value: number): string => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const handleSubmit = async () => {
    if (!isValid || !selectedMethod) return;

    setIsSubmitting(true);
    try {
      const result = await createPartialPayment({
        orderId,
        paymentMethodId: selectedMethodId,
        amount: numericAmount,
        feeAmount,
        payerName: payerName.trim() || undefined,
      });

      if (result && printReceipt && partialPaymentPrinter) {
        await printPartialPaymentReceipt(
          {
            restaurantName: activeRestaurant?.name || "Restaurante",
            tableNumber,
            tabNumber,
            commandNumber,
            payerName: payerName.trim() || undefined,
            paymentMethodName: selectedMethod.name,
            amount: numericAmount,
            orderNumber,
            createdAt: new Date(),
          },
          partialPaymentPrinter.printerName,
          partialPaymentPrinter.charsPerLine
        );
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error creating partial payment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pagamento Parcial</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Remaining amount info */}
          <div className="rounded-lg bg-muted p-3 text-center">
            <p className="text-sm text-muted-foreground">Valor restante</p>
            <p className="text-2xl font-bold">
              {formatCurrency(remainingAmount)}
            </p>
          </div>

          {/* Payment method selection */}
          <div className="space-y-2">
            <Label>Método de Pagamento</Label>
            <div className="grid grid-cols-2 gap-2">
              {activePaymentMethods.map((method) => {
                const Icon = paymentTypeIcons[method.type] || CreditCard;
                const isSelected = selectedMethodId === method.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethodId(method.id)}
                    className={`flex items-center gap-2 rounded-lg border p-3 transition-colors ${
                      isSelected
                        ? "border-primary bg-primary/10"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{method.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount input */}
          <div className="space-y-2">
            <Label htmlFor="amount">Valor</Label>
            <Input
              id="amount"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="text-lg"
            />
            {numericAmount > remainingAmount && (
              <p className="text-sm text-destructive">
                O valor não pode ser maior que o restante
              </p>
            )}
            {selectedMethod?.fee_percentage ? (
              <p className="text-xs text-muted-foreground">
                Taxa: {formatCurrency(feeAmount)} ({selectedMethod.fee_percentage}%)
              </p>
            ) : null}
          </div>

          {/* Payer name */}
          <div className="space-y-2">
            <Label htmlFor="payerName">Nome do Pagante (opcional)</Label>
            <Input
              id="payerName"
              type="text"
              placeholder="Ex: João"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
            />
          </div>

          {/* Print receipt toggle */}
          {partialPaymentPrinter && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Printer className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="printReceipt">Imprimir comprovante</Label>
              </div>
              <Switch
                id="printReceipt"
                checked={printReceipt}
                onCheckedChange={setPrintReceipt}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registrando...
              </>
            ) : (
              `Registrar ${numericAmount > 0 ? formatCurrency(numericAmount) : ""}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
